import * as React from "react";
import { useState, } from "react";

export interface IconContainerProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: number;
}

export interface RatingProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, "onChange" | "children"> {
  defaultValue?: number;
  disabled?: boolean;
  emptyIcon?: React.ReactNode;
  emptyLabelText?: React.ReactNode;
  getLabelText?: (value: number) => string;
  icon?: React.ReactNode;
  IconContainerComponent?: React.ElementType<IconContainerProps>;
  max?: number;
  name?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, value: number | null) => void;
  onChangeActive?: (event: React.MouseEvent, value: number) => void;
  precision?: number;
  readOnly?: boolean;
  size?: "small" | "medium" | "large";
  value?: number | null;
}

// Ícone de Estrela Padrão SVG
const DefaultStar = ({ filled, sizeClass }: { filled: boolean; sizeClass: string }) => (
  <svg
    className={`${sizeClass} transition-colors duration-200 ${
      filled ? "text-yellow-400" : "text-gray-300"
    }`}
    fill="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

export default function Rating(props: RatingProps): JSX.Element {
  const {
    defaultValue = null,
    disabled = false,
    emptyIcon,
    icon,
    max = 5,
    name = "rating",
    onChange,
    onChangeActive,
    readOnly = false,
    size = "medium",
    value: controlledValue,
    className = "",
    ...other
  } = props;

  // Gerenciamento de estado para componente controlado vs não controlado
  const [uncontrolledValue, setUncontrolledValue] = useState<number | null>(defaultValue);
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : uncontrolledValue;

  // O valor exibido reage ao hover; se não houver hover, mostra o valor selecionado
  const displayValue = hoverValue !== null ? hoverValue : (currentValue || 0);

  // Mapeamento de tamanhos para classes do Tailwind
  const sizeClasses = {
    small: "w-4 h-4",
    medium: "w-6 h-6",
    large: "w-8 h-8",
  };
  const currentSizeClass = sizeClasses[size] || sizeClasses.medium;

  const handleMouseEnter = (event: React.MouseEvent, index: number) => {
    if (disabled || readOnly) return;
    setHoverValue(index);
    if (onChangeActive) {
      onChangeActive(event, index);
    }
  };

  const handleMouseLeave = (event: React.MouseEvent) => {
    if (disabled || readOnly) return;
    setHoverValue(null);
    if (onChangeActive) {
      onChangeActive(event, -1);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (disabled || readOnly) return;
    if (!isControlled) {
      setUncontrolledValue(index);
    }
    if (onChange) {
      onChange(event, index);
    }
  };

  // Configuração das classes base do container
  const baseClasses = "inline-flex items-center";
  const stateClasses = disabled
    ? "opacity-50 cursor-default pointer-events-none"
    : readOnly
      ? "cursor-default pointer-events-none"
      : "cursor-pointer";

  return (
    <span
      className={`${baseClasses} ${stateClasses} ${className}`}
      onMouseLeave={handleMouseLeave}
      role="radiogroup"
      {...other}
    >
      {Array.from({ length: max }).map((_, index) => {
        const itemValue = index + 1;
        const isFilled = itemValue <= displayValue;

        const renderedIcon = isFilled
          ? (icon || <DefaultStar filled={true} sizeClass={currentSizeClass} />)
          : (emptyIcon || <DefaultStar filled={false} sizeClass={currentSizeClass} />);

        return (
          <label
            key={itemValue}
            className={`relative flex items-center justify-center m-0 p-0 ${
              !disabled && !readOnly ? "transition-transform hover:scale-110" : ""
            }`}
            onMouseEnter={(e) => handleMouseEnter(e, itemValue)}
          >
            <input
              type="radio"
              name={name}
              value={itemValue}
              checked={currentValue === itemValue}
              disabled={disabled || readOnly}
              onChange={(e) => handleChange(e, itemValue)}
              className="sr-only"
            />

            <span className="flex items-center justify-center">
              {renderedIcon}
            </span>
          </label>
        );
      })}
    </span>
  );
}